/**
 * English and Arabic, and the direction that comes with them.
 *
 * Direction is set on `<html>` rather than per-component, so every layout built on logical
 * CSS properties — `margin-inline-start`, not `margin-left` — flips without a single
 * conditional. A component that reaches for `left` is a component that will be wrong in
 * Arabic, and the lint rule that catches it is worth more than any amount of review.
 *
 * Arabic is a stated requirement for a government client in the UAE. Whether their team
 * uses it daily is still an open question, but a product that cannot be read in Arabic
 * cannot be sold there at all, and retrofitting direction into a finished UI costs an order
 * of magnitude more than carrying it from the first screen.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import ar from './ar.json'
import en from './en.json'

export const LANGUAGES = ['en', 'ar'] as const
export type Language = (typeof LANGUAGES)[number]

const RTL: ReadonlySet<string> = new Set(['ar'])

export function isRtl(language: string): boolean {
  return RTL.has(language)
}

/** Put the language and its direction on the document. Called on load and on change. */
export function applyLanguage(language: Language): void {
  const root = document.documentElement
  root.lang = language
  root.dir = isRtl(language) ? 'rtl' : 'ltr'
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, ar: { translation: ar } },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  // A missing key should be loud in development and harmless in production: showing the
  // key beats showing an empty space where a label belongs.
  returnEmptyString: false,
})

i18n.on('languageChanged', (language) => {
  applyLanguage(LANGUAGES.includes(language as Language) ? (language as Language) : 'en')
})

export default i18n
